// src/contracts/components/ProseBlock.js
// Read-only paragraph block that swaps to a contenteditable editor when the
// pencil icon is clicked. Exhibit references in prose use the token format
// {ref:exhibitId}; on render they're swapped for "EXHIBIT X" badges that update
// live when the exhibits array changes. Deleted references show as a red badge.
//
// We intentionally avoid using a custom Quill blot — same UX, simpler
// implementation. The "Insert exhibit reference" button writes the token into
// the active selection so it round-trips cleanly through draft saves.

import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Check, Bookmark, ChevronDown, AlertTriangle } from 'lucide-react';

const REF_REGEX = /\{ref:([^}]+)\}/g;

function letterOf(i) { return String.fromCharCode(65 + i); }

// Convert a paragraph string with {ref:...} tokens into React nodes that show
// the resolved letter and a broken/valid style.
function renderResolvedParagraph(text, exhibits) {
  const idx = {};
  exhibits.forEach((ex, i) => { idx[ex.exhibitId] = letterOf(i); });

  const parts = [];
  let last = 0;
  const re = new RegExp(REF_REGEX.source, 'g');
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const id = m[1];
    const letter = idx[id];
    parts.push(
      <span
        key={`${m.index}-${id}`}
        className={`inline-flex items-baseline px-1.5 mx-0.5 rounded ${
          letter
            ? 'bg-indigo-950/60 text-indigo-200'
            : 'bg-red-950/40 text-red-300'
        }`}
        title={letter ? `Reference to exhibit ${letter}` : 'Reference points to a deleted exhibit'}
      >
        EXHIBIT {letter || '(deleted)'}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// Round-trip helper: editor uses real <span data-ref="id">EXHIBIT X</span> for
// inline references; we convert to / from the {ref:id} token format on save.
function tokensToHtml(text) {
  return text.replace(REF_REGEX, (_, id) =>
    `<span class="se-ref" data-ref="${id}" contenteditable="false">EXHIBIT ?</span>`
  );
}
function htmlToTokens(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('span[data-ref]').forEach(s => {
    const id = s.getAttribute('data-ref');
    s.replaceWith(`{ref:${id}}`);
  });
  return div.innerText.replace(/ /g, ' ');
}

function ProseParagraph({ text, exhibits, onChange, onDelete, exhibitsList }) {
  const [editing, setEditing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const editorRef = useRef(null);

  // Backspace inside an empty paragraph/bullet removes that item entirely.
  // Standard list-editor behavior (Notion, Google Docs, etc.) and the cleanest
  // way to handle "I deleted the text but the empty bullet won't go away".
  const onKeyDown = (e) => {
    if (e.key !== 'Backspace' || !onDelete) return;
    const el = editorRef.current;
    if (!el) return;
    // textContent is empty (we don't count ref spans as "content" for this
    // purpose, but if any are present we leave them be — the user should
    // explicitly clear them first)
    if (el.textContent.trim() === '' && !el.querySelector('span[data-ref]')) {
      e.preventDefault();
      setEditing(false);
      onDelete();
    }
  };

  useEffect(() => {
    if (editing && editorRef.current) {
      // Render with placeholder spans for refs, then immediately fix the letters
      editorRef.current.innerHTML = tokensToHtml(text || '');
      // Update each ref span's visible letter based on current exhibits
      const idx = {};
      exhibitsList.forEach((ex, i) => { idx[ex.exhibitId] = letterOf(i); });
      editorRef.current.querySelectorAll('span[data-ref]').forEach(s => {
        const id = s.getAttribute('data-ref');
        s.textContent = `EXHIBIT ${idx[id] || '(deleted)'}`;
        s.classList.toggle('text-red-300', !idx[id]);
        s.classList.toggle('text-indigo-200', !!idx[id]);
      });
      editorRef.current.focus();
    }
  }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = () => {
    if (!editorRef.current) return setEditing(false);
    const tokens = htmlToTokens(editorRef.current.innerHTML);
    onChange(tokens);
    setEditing(false);
  };

  const insertRef = (exhibitId) => {
    editorRef.current?.focus();
    const span = document.createElement('span');
    span.className = 'se-ref bg-indigo-950/60 text-indigo-200 px-1.5 mx-0.5 rounded';
    span.setAttribute('data-ref', exhibitId);
    span.setAttribute('contenteditable', 'false');
    const idx = exhibitsList.findIndex(e => e.exhibitId === exhibitId);
    span.textContent = `EXHIBIT ${idx === -1 ? '(deleted)' : letterOf(idx)}`;
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(span);
      // Move cursor after the inserted span
      range.setStartAfter(span);
      range.setEndAfter(span);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editorRef.current.appendChild(span);
    }
    setPickerOpen(false);
  };

  const fmt = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
  };

  if (editing) {
    return (
      <div className="rounded-lg border border-indigo-700/60 bg-indigo-950/10">
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-indigo-800/40 bg-indigo-950/20">
          <button type="button" onMouseDown={(e) => { e.preventDefault(); fmt('bold'); }} className="px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-800 rounded font-bold">B</button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); fmt('italic'); }} className="px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-800 rounded italic">I</button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); fmt('insertUnorderedList'); }} className="px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-800 rounded">•</button>
          <div className="relative">
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); setPickerOpen(o => !o); }}
              className="px-2 py-1 text-xs text-indigo-300 hover:bg-zinc-800 rounded inline-flex items-center gap-1"
            >
              <Bookmark className="w-3 h-3" /> insert exhibit ref <ChevronDown className="w-3 h-3" />
            </button>
            {pickerOpen && (
              <div className="absolute top-7 left-0 bg-zinc-900 border border-zinc-700 rounded-md py-1 z-30 min-w-[220px] max-h-72 overflow-y-auto">
                {exhibitsList.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">no exhibits</div>}
                {exhibitsList.map((ex, i) => (
                  <button
                    key={ex.exhibitId}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertRef(ex.exhibitId); }}
                    className="w-full text-left px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
                  >
                    <span className="text-indigo-300 font-medium">EXHIBIT {letterOf(i)}</span>
                    <span className="text-zinc-500 ml-2">{ex.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={commit}
            className="px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900/40 rounded inline-flex items-center gap-1"
          >
            <Check className="w-3 h-3" /> done
          </button>
        </div>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={onKeyDown}
          className="px-3 py-2.5 text-[13px] leading-relaxed text-zinc-100 outline-none min-h-[70px]"
        />
      </div>
    );
  }

  return (
    <div className="group relative px-3 py-2 rounded-lg hover:bg-gray-950/40 border border-transparent hover:border-zinc-800 transition">
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-zinc-400 hover:text-indigo-300"
        aria-label="Edit paragraph"
        title="Edit"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <p className="text-[13px] leading-relaxed text-zinc-200">
        {renderResolvedParagraph(text || '', exhibits)}
      </p>
    </div>
  );
}

// Tiny click-to-edit heading. Click → input on the same line; Enter or blur
// commits, Escape cancels. Used by both ProseBlock and BulletsBlock so every
// section heading is editable per the "all text should be editable" rule.
export function EditableHeading({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };
  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  if (!editing) {
    return (
      <h3
        className="group inline-flex items-center gap-2 text-sm font-semibold text-white tracking-wide pt-2 cursor-text"
        onClick={() => { setDraft(value || ''); setEditing(true); }}
        title="Click to rename"
      >
        <span>{value || <span className="text-zinc-600 italic">(untitled)</span>}</span>
        <Pencil className="w-3 h-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
      </h3>
    );
  }
  return (
    <input
      ref={inputRef}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
      }}
      className="text-sm font-semibold text-white tracking-wide pt-2 bg-transparent border-b border-indigo-500 outline-none w-full max-w-2xl"
    />
  );
}

// Amber pill displayed next to the heading when a section / exhibit is flagged
// as containing statutorily-mandated language. Content still fully editable —
// this is just a heads-up.
export function RegulatedBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium rounded-full bg-amber-900/40 text-amber-300 border border-amber-700/40">
      <AlertTriangle className="w-2.5 h-2.5" />
      California HIC required language
    </span>
  );
}

export default function ProseBlock({ heading, paragraphs, exhibits, regulated, onChangeHeading, onChangeParagraphs }) {
  const updateParagraph = (idx, value) => {
    const next = paragraphs.slice();
    next[idx] = value;
    onChangeParagraphs(next);
  };
  const removeParagraph = (idx) => {
    const next = paragraphs.slice();
    next.splice(idx, 1);
    onChangeParagraphs(next);
  };

  return (
    <div className="space-y-1">
      {(heading !== undefined || regulated) && (
        <div className="flex flex-wrap items-center gap-2">
          {heading !== undefined && (
            onChangeHeading
              ? <EditableHeading value={heading} onChange={onChangeHeading} />
              : <h3 className="text-sm font-semibold text-white tracking-wide pt-2">{heading}</h3>
          )}
          {regulated && <RegulatedBadge />}
        </div>
      )}
      {(paragraphs || []).map((p, i) => (
        <ProseParagraph
          key={i}
          text={p}
          exhibits={exhibits}
          exhibitsList={exhibits}
          onChange={(v) => updateParagraph(i, v)}
          onDelete={() => removeParagraph(i)}
        />
      ))}
    </div>
  );
}

export function BulletsBlock({ heading, items, exhibits, regulated, onChangeHeading, onChangeItems }) {
  const updateItem = (idx, value) => {
    const next = items.slice();
    next[idx] = value;
    onChangeItems(next);
  };
  const removeItem = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    onChangeItems(next);
  };

  return (
    <div className="space-y-1">
      {(heading !== undefined || regulated) && (
        <div className="flex flex-wrap items-center gap-2">
          {heading !== undefined && (
            onChangeHeading
              ? <EditableHeading value={heading} onChange={onChangeHeading} />
              : <h3 className="text-sm font-semibold text-white tracking-wide pt-2">{heading}</h3>
          )}
          {regulated && <RegulatedBadge />}
        </div>
      )}
      <ul className="space-y-1 pl-4">
        {(items || []).map((item, i) => (
          <li key={i} className="flex gap-2 items-start">
            <span className="text-zinc-500 mt-2.5">•</span>
            <div className="flex-1">
              <ProseParagraph
                text={item}
                exhibits={exhibits}
                exhibitsList={exhibits}
                onChange={(v) => updateItem(i, v)}
                onDelete={() => removeItem(i)}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
